// The bar every screen wears: the wordmark on the left, wherever you are;
// the name of what is open in the middle; the controls that belong to the
// whole app on the right.
//
// Two things are always reachable from here, because they belong to no screen
// in particular: the wordmark goes home to the library, and the sliders open
// the settings. Everything else is local. Going back one step is a disc beside
// the text (design §14.2), and everything a document can do is in the dock.

import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { Link } from "react-router-dom";

import { APP_NAME } from "./brand";
import { Icon, Logo } from "./Icon";
import { toggleTheme, useTheme } from "./theme";

export function TopBar({
  title,
  children,
  /** Only the settings screen turns this off: a button that goes where you
   *  already are is a button that reads as broken. */
  settings = true,
}: {
  title?: ReactNode;
  children?: ReactNode;
  settings?: boolean;
}) {
  return (
    <header className="topbar">
      <Link to="/" className="brand" aria-label={`${APP_NAME} — back to the library`}>
        <Logo size={20} />
        <span className="wordmark">{APP_NAME}</span>
      </Link>
      <div className="topbar-title">{title}</div>
      <div className="topbar-actions">
        {children}
        {settings && (
          <Link to="/settings" className="icon-button" aria-label="Settings" title="Settings">
            <Icon name="sliders" size={24} />
          </Link>
        )}
        <ThemeToggle />
      </div>
    </header>
  );
}

/** The way back: a disc in the lane to the left of the text (design §14.2).
 *  It is where the eye already is, and it never competes with the wordmark. */
export function BackDisc({ to = "/", label = "Back to the library" }: { to?: string; label?: string }) {
  return (
    <div className="backrow">
      <Link to={to} className="back-disc" aria-label={label} title={label}>
        <Icon name="back" size={24} />
      </Link>
    </div>
  );
}

export function ThemeToggle() {
  const theme = useTheme();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button
      type="button"
      className="icon-button"
      onClick={toggleTheme}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
    >
      <Icon name={theme === "dark" ? "sun" : "moon"} size={24} />
    </button>
  );
}

/** A round icon button. `primary` is the accented disc that marks the one
 *  action a screen is really offering. */
export function IconButton({
  icon,
  label,
  onClick,
  primary,
  active,
  disabled,
  busy,
  size,
  hold,
}: {
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  onClick: () => void;
  primary?: boolean;
  active?: boolean;
  disabled?: boolean;
  busy?: boolean;
  size?: number;
  /** Anything extra the button should carry: the pointer handlers, the menu
   *  wiring and a ref, for a button that has a second thing to offer when it
   *  is held down. One prop rather than six, because only one button has it.
   *  The ref is how a menu gives focus back when it closes. */
  hold?: ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> };
}) {
  const classes = ["icon-button"];
  if (primary) classes.push("primary");
  if (active) classes.push("active");
  if (busy) classes.push("busy");
  return (
    <button
      {...hold}
      type="button"
      className={classes.join(" ")}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}
